import { Edit3, Copy, Trash2, X, Settings } from 'lucide-react';
import { BLOCK_SCHEMAS } from './blocks';
import { EDIT_FORMS, Field, TextInput, TextArea } from './forms';
import { countWords } from './blocks';
import type { Block, Draft } from '@shared/types';

export function EditPanel({
  block,
  draftId,
  onChange,
  onClose,
  onDelete,
  onDuplicate,
}: {
  block: Block | undefined;
  draftId: string;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  if (!block) {
    return (
      <div className="w-80 border-l border-slate-200 bg-white flex-shrink-0 flex items-center justify-center text-center px-6">
        <div className="text-slate-400">
          <Edit3 size={20} className="mx-auto mb-2 opacity-60" />
          <p className="text-sm font-medium text-slate-600">No block selected</p>
          <p className="text-xs mt-1 text-slate-500">Click any block in the preview to edit it.</p>
        </div>
      </div>
    );
  }
  const FormComponent = EDIT_FORMS[block.type];
  const schema = BLOCK_SCHEMAS[block.type];
  const Icon = schema.icon;
  return (
    <div className="w-80 border-l border-slate-200 bg-white flex-shrink-0 flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-shrink-0">
        <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
          <Icon size={13} className="text-blue-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{schema.label}</div>
          <div className="text-[11px] text-slate-500 truncate">{schema.description}</div>
        </div>
        <button onClick={onDuplicate} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Duplicate"><Copy size={12} /></button>
        <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded text-slate-500 hover:text-red-600" title="Delete"><Trash2 size={12} /></button>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><X size={13} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <FormComponent data={block.data} onChange={(d) => onChange(d as Record<string, unknown>)} draftId={draftId} />
      </div>
    </div>
  );
}

export function SettingsPanel({
  draft,
  onSubjectChange,
  onPreheaderChange,
  onClose,
}: {
  draft: Draft;
  onSubjectChange: (v: string) => void;
  onPreheaderChange: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="w-80 border-l border-slate-200 bg-white flex-shrink-0 flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <Settings size={14} className="text-slate-700" />
        <div className="text-sm font-semibold text-slate-900 flex-1">Email metadata</div>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><X size={13} /></button>
      </div>
      <div className="p-4 flex-1 overflow-y-auto">
        <Field label="Subject line" hint="What recipients see in their inbox first.">
          <TextInput value={draft.subject || ''} onChange={onSubjectChange} placeholder="It's all happening in Bangor today" />
        </Field>
        <Field label="Preheader" hint="Preview text next to the subject in most clients. ~80 characters.">
          <TextArea value={draft.preheader || ''} onChange={onPreheaderChange} rows={2} placeholder="A peek at what's inside today's edition." />
        </Field>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Personalization</div>
          <p className="text-xs text-slate-600 mb-2">
            Type these tokens in any text field — they're swapped per recipient at send time.
          </p>
          <ul className="space-y-1 text-xs">
            <li><code className="bg-slate-100 px-1 py-0.5 rounded">{'{{first_name}}'}</code> <span className="text-slate-500">— recipient's first name</span></li>
            <li><code className="bg-slate-100 px-1 py-0.5 rounded">{'{{first_name|Friend}}'}</code> <span className="text-slate-500">— with fallback</span></li>
            <li><code className="bg-slate-100 px-1 py-0.5 rounded">{'{{last_name}}'}</code></li>
            <li><code className="bg-slate-100 px-1 py-0.5 rounded">{'{{email}}'}</code></li>
            <li><code className="bg-slate-100 px-1 py-0.5 rounded">{'{{unsubscribe_url}}'}</code> <span className="text-slate-500">— auto-injected in footer</span></li>
          </ul>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Stats</div>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Blocks</span><span className="font-medium text-slate-800">{draft.blocks.length}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Word count</span><span className="font-medium text-slate-800">{countWords(draft.blocks)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Read time</span><span className="font-medium text-slate-800">~{Math.max(1, Math.round(countWords(draft.blocks) / 220))} min</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Last updated</span><span className="font-medium text-slate-800">{new Date(draft.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
